import React from 'react'

import { Box, Grid, Paper } from '@mui/material'
import { endOfMonth, startOfMonth } from 'date-fns'

import { CardTitle } from 'components/common/controls'
import { ReportFileType, ReportFileTypeValues } from 'constants/EntityConstants'
import {
  SupplierPaymentOverviewReportInputDtoInput,
  useGetSupplierPaymentOverviewReportQuery,
} from 'generated/graphql'
import { GetFileName, SaveFile, ShowRestError } from 'helpers'
import { DownloadSupplierPaymentOverviewReport } from 'services/RestClient'

import { ReportTable } from '../common/ReportTable'
import FilterCriteria from './FilterCriteria'

const defaultFilter = {
  start: startOfMonth(new Date()),
  end: endOfMonth(new Date()),
}

const SupplierPaymentOverview = () => {
  const [isProcessing, setIsProcessing] = React.useState(false)
  const [filter, setFilter] =
    React.useState<SupplierPaymentOverviewReportInputDtoInput>(defaultFilter)

  const { data: supplierPaymentOverviewReportData, fetchMore } =
    useGetSupplierPaymentOverviewReportQuery({
      variables: { input: filter },
    })

  React.useEffect(() => {
    fetchMore({ variables: { input: filter } })
  }, [filter, fetchMore])

  const headerRow = React.useMemo(
    () => supplierPaymentOverviewReportData?.supplierPaymentOverviewReport.rows[0],
    [supplierPaymentOverviewReportData],
  )

  const bodyRows = React.useMemo(
    () =>
      supplierPaymentOverviewReportData?.supplierPaymentOverviewReport.rows.filter((_, index) => {
        if (index !== 0) {
          return true
        }
      }),
    [supplierPaymentOverviewReportData],
  )

  const handleViewReport = (filter: SupplierPaymentOverviewReportInputDtoInput) => {
    setFilter(filter)
  }

  const handleDownloadReport = (
    type: ReportFileType,
    filter: SupplierPaymentOverviewReportInputDtoInput,
  ) => {
    setIsProcessing(true)

    DownloadSupplierPaymentOverviewReport(type, filter)
      .then((response) => {
        setIsProcessing(false)

        let fileName = GetFileName(response)
        if (!fileName) {
          const format = ReportFileTypeValues.find((reportType) => reportType.id === type)?.name
          fileName = `supplier-payment-overview-report.${format}`
        }

        SaveFile(response.data, fileName)
      })
      .catch((error) => {
        ShowRestError(error)
      })
      .finally(() => {
        setIsProcessing(false)
      })
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper variant='outlined'>
          <Box p={2}>
            <CardTitle>Filter Criteria</CardTitle>
            <FilterCriteria
              defaultFilter={defaultFilter}
              isProcessing={isProcessing}
              onViewReport={handleViewReport}
              onDownloadReport={handleDownloadReport}
            />
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper variant='outlined'>
          <Box p={2}>
            <CardTitle>Report Table</CardTitle>
            <ReportTable headerRow={headerRow} bodyRows={bodyRows} />
          </Box>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default SupplierPaymentOverview
